var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import BemClassBuilder from '../../../core/utils/src/class-builders/bem';
import { ExpandableSection } from '../../expandable-section/src';
import { Icon } from '../../icon/src';
import { HEADER, DIVIDER, LINK, SECTION, LINK_GROUP, EXPANDABLE_LINK_GROUP, validateDataFormat, validateItems, hasActiveLink, generateExpandableItemsIndex } from './utils';
export var SideNavigation = AwsUi.__addComponent('awsui-side-navigation', {
    releaseStatus: 'stable',
    properties: {
        // Object responsible for the header at the top of the navigation component.
        // It contains:
        // * `text` [string]: text to be displayed as a header.
        // * `href` [string]: href the header should link to.
        header: {
            type: 'object',
            objectType: 'SideNavigation.Header',
            onChange: function (oldHeader, newHeader) {
                if (newHeader) {
                    validateDataFormat(newHeader, HEADER);
                }
            }
        },
        // Href of the currently active link.
        // All items within the navigation with matching href will be highlighted.
        // `Sections` and `Expandable Page Groups` which contain a highlighted item will be automatically expanded,
        // unless their definitions have `expanded` property explicitly set to `false`.
        // `activeHref` property is automatically set to the href of a clicked item, unless `follow` event is prevented.
        activeHref: {
            type: 'string'
        },
        // Items to be displayed in the navigation.
        // Allowed objects are `Link`, `Divider`, `Section`, `LinkGroup` and `ExpandableLinkGroup`.
        // For details on when to use each of the items, see UX documentation.
        //
        // Keep in mind that you can always inject extra properties (for example: an id)
        // in order to more easily identify the item when it's used in an event `detail`
        // (see the events section below for more information).
        //
        // #### Link
        // Object that represents an anchor in the navigation.
        // Links are rendered as `<a>` tags.
        // * `type`: `'link'`.
        // * `text` [string]: text of the link.
        // * `href` [string]: href of the link.
        // * `external` [boolean]: if set to `true` the navigation item will be decorated with an icon marking the link as external.
        //   The anchor will also receive the attributes `target="_blank"` and `rel="noopener"`. Additionally, `activeHref` property will not be modified when a user clicks on the link.
        // * `info` [Renderable]: allows to display content next to the link. Although it is technically possible to insert any content,
        //   our UX guidelines allow only to add a Badge. The definition of "renderable" depends on your framework, see below.
        //
        // #### Divider
        // Object that represents a horizontal divider between navigation content.
        // It only contains
        // * `type`: `'divider'`.
        //
        // #### Section
        // Object that represents a section within the navigation.
        // * `type`: `'section'`.
        // * `text` [string]: text to be displayed as a title of the section.
        // * `expanded` [boolean]: whether section should be expanded or not. Default value is `true`.
        // * `items` [array]: a content of the section. Can be any valid item from this list.
        //   Although there is no technical limitation to the nesting level,
        //   our UX recommendation is to use only one level.
        //
        // #### LinkGroup
        // Object that represents a group of links.
        // * `type`: `'link-group'`.
        // * `text` [string]: text of the group link.
        // * `href` [string]: href of the group link.
        // * `items` [array]: array of child content. Can be any valid item from this list.
        //   Although there is no technical limitation to the nesting level,
        //   our UX recommendation is to use only one level.
        //
        // #### ExpandableLinkGroup
        //
        // Object that represents an expandable group of links.
        // * `type`: `'expandable-link-group'`.
        // * `text` [string]: text of the group link.
        // * `href` [string]: href of the group link.
        // * `expanded` [boolean]: whether group should be expanded or not.
        //   If not explicitly set, the group will be collapsed by default,
        //   unless one of the nested links is active.
        // * `items` [array]: array of child content.
        //   Can be any valid item from this list.
        //   Although there is no technical limitation to the nesting level,
        //   our UX recommendation is to use only one level.
        //
        //
        // #### Renderable value
        // * **AngularJS:** Must be a string representing an Angular template.
        //   Example: `'<awsui-badge>2</awsui-badge>'`. **Security note:** the return value will be treated as HTML,
        //   make sure you have escaped the user input using [ngBind](https://docs.angularjs.org/api/ng/directive/ngBind)
        //   or [interpolation](https://docs.angularjs.org/guide/interpolation).
        // * **React:** Must be a valid React element or a function that returns a React element to display.
        //   Example: `() => <Badge>2</Badge>` or just `<Badge>2</Badge>` in jsx.
        // * **GWT:** Must be a lambda function that should return either a string, html element or GWT-widget instance.
        //   Example: `() -> 'some text'` or `() -> new HTMLPanel()`.
        items: {
            type: 'array',
            itemTypes: [
                'SideNavigation.Item',
                'SideNavigation.Link',
                'SideNavigation.Divider',
                'SideNavigation.Section',
                'SideNavigation.LinkGroup',
                'SideNavigation.ExpandableLinkGroup'
            ],
            onChange: function (oldItems, newItems) {
                if (oldItems === newItems) {
                    return;
                }
                validateItems(newItems);
                this.__expandedStateIndex = generateExpandableItemsIndex(newItems);
            }
        }
    },
    events: {
        // Fired when an anchor is clicked without any modifier (CTRL, ALT, SHIFT).
        // The event `detail` contains a definition of the clicked item.
        // Use this event to prevent default browser navigation (by calling `preventDefault` method)
        // and branch your own routing.
        //
        // If the event is prevented the `activeHref` property will not be automatically set
        // to the href of the clicked item, so you will have to do it yourself.
        follow: {
            bubbles: true,
            cancelable: true,
            detailType: 'SideNavigation.FollowDetail'
        },
        // Fired when expansion state of `Section` or `ExpandablePageGroup` items changes
        // as a result of a user interaction.
        // The event `detail` contains an object with information about the changed item.
        // * `item` [object]: definition of the item which was changed.
        // * `expanded` [boolean]: whether the item is expanded or not.
        // * `expandableParents` [array]: a list of parent items which has type of `Section` or `ExpandablePageGroup`.
        //   Use `expandableParents` array to set their expanded state to `true` if you want
        //   your data model to keep track of the current state of the navigation items.
        //
        // _Remark:_ the `awsui:change` event is not raised if the expansion of expandable items
        // is merely a result of the activation of a nested link upon changing the `activeHref` property.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'SideNavigation.ChangeDetail'
        }
    },
    render: function (m, h) {
        var _this = this;
        var getLocalState = function (def) {
            return _this.__expandedStateIndex.filter(function (_a) {
                var item = _a.item;
                return item === def;
            })[0];
        };
        var createDivider = function (modifier) {
            if (modifier === void 0) { modifier = 'default'; }
            var className = new BemClassBuilder(_this, DIVIDER).modifier(modifier).toString();
            return AwsUi.__createElement("hr", { className: className });
        };
        var createList = function (items, modifier) {
            if (items === void 0) { items = []; }
            if (modifier === void 0) { modifier = ''; }
            // value must me initialized, undefined will not work because of mithril issue:
            // https://github.com/MithrilJS/mithril.js/issues/2258
            var className = null;
            if (modifier) {
                className = new BemClassBuilder(_this, 'list').modifier(modifier).toString();
            }
            return (AwsUi.__createElement("ul", { className: className }, items.map(function (item, index) { return (AwsUi.__createElement("li", { lifecycleKey: index }, navigationItemsFactory(item))); })));
        };
        var createClickHandler = function (definition) { return function (event) {
            // if the event is not stopped it propagates to expandable section and changes state set during rendering
            event.stopPropagation();
            // only if primary mouse button is clicked without any modifier keys
            var specialKey = event.button !== 0 || event.ctrlKey || event.altKey || event.shiftKey || event.metaKey;
            if (specialKey) {
                return;
            }
            if (definition.type === EXPANDABLE_LINK_GROUP) {
                var localState = getLocalState(definition);
                if (!localState.expanded) {
                    localState.expanded = true;
                    _this.__fireEvent('change', localState);
                }
                _this.__update();
            }
            _this.__fireEvent('follow', definition, {
                nativeEvent: event,
                onNotCanceled: function () {
                    if (!definition.external) {
                        _this.activeHref = definition.href;
                    }
                }
            });
        }; };
        var createChangeHandler = function (definition) { return function (event) {
            event.stopPropagation();
            var localState = getLocalState(definition);
            localState.expanded = event.detail.expanded;
            _this.__fireEvent('change', localState);
        }; };
        var createKeyupHandler = function (definition) { return function (event) {
            if (event.keyCode === 13) {
                event.stopPropagation();
            }
        }; };
        var createLink = function (def, expanded) {
            var infoContent = def.info ? (AwsUi.__createElement("span", { className: "awsui-util-ml-s" }, _this.renderToMithril("link-item-" + def.href + "-" + def.text, def.info))) : null;
            var externalIcon = def.external ? AwsUi.__createElement(Icon, { name: "external", className: "awsui-util-ml-s" }) : null;
            var isActive = _this.activeHref === def.href;
            var className = new BemClassBuilder(_this, LINK).modifier('active', isActive).toString();
            var attributes = {
                onclick: createClickHandler(def),
                onkeyup: createKeyupHandler(def),
                className: className,
                href: def.href
            };
            if (def.external) {
                attributes.target = '_blank';
                attributes.rel = 'noopener';
            }
            if (isActive) {
                attributes['aria-current'] = 'page';
            }
            if (expanded !== undefined) {
                attributes['aria-expanded'] = "" + expanded;
            }
            return (AwsUi.__createElement("span", null,
                AwsUi.__createElement("a", __assign({}, attributes),
                    def.text,
                    externalIcon),
                infoContent));
        };
        var createSection = function (def) {
            var localState = getLocalState(def);
            var expanded = localState.expanded || localState.expanded === undefined;
            var list = createList(def.items);
            var header = AwsUi.__createElement("div", { className: "awsui-side-navigation__title" }, def.text);
            var onChange = createChangeHandler(def);
            var subNavClassName = new BemClassBuilder(_this, 'sub-navigation')
                .modifier(SECTION)
                .utility('no-gutters')
                .toString();
            return (AwsUi.__createElement(ExpandableSection, { className: subNavClassName, variant: "borderless", header: header, content: list, expanded: expanded, change: onChange }));
        };
        var createLinkGroup = function (def) {
            var header = createLink(def);
            var list = createList(def.items);
            return (AwsUi.__createElement("div", null,
                header,
                list));
        };
        var createExpandableLinkGroup = function (def) {
            var localState = getLocalState(def).expanded;
            var expanded = localState !== undefined ? localState : hasActiveLink(def, _this.activeHref);
            var header = createLink(def, expanded);
            var list = createList(def.items, EXPANDABLE_LINK_GROUP);
            var onChange = createChangeHandler(def);
            var subNavClassName = new BemClassBuilder(_this, 'sub-navigation').modifier(EXPANDABLE_LINK_GROUP).toString();
            return (AwsUi.__createElement(ExpandableSection, { variant: "navigation", className: subNavClassName, header: header, content: list, expanded: expanded, change: onChange }));
        };
        var createHeader = function (def) {
            if (!def) {
                // do not render header if we have no data
                return null;
            }
            var attributes = {
                onclick: createClickHandler(def),
                href: def.href,
                className: 'awsui-side-navigation__header-link'
            };
            if (def.href === _this.activeHref) {
                attributes['aria-current'] = 'page';
            }
            return (AwsUi.__createElement("div", null,
                AwsUi.__createElement("div", { className: "awsui-side-navigation__header awsui-util-font-size-3" },
                    AwsUi.__createElement("a", __assign({}, attributes), def.text)),
                createDivider('header')));
        };
        var navigationItemsFactory = function (def) {
            switch (def.type) {
                case LINK:
                    return createLink(def);
                case DIVIDER:
                    return createDivider();
                case SECTION:
                    return createSection(def);
                case LINK_GROUP:
                    return createLinkGroup(def);
                case EXPANDABLE_LINK_GROUP:
                    return createExpandableLinkGroup(def);
            }
        };
        var header = createHeader(this.header);
        var list = createList(this.items, 'root');
        return (AwsUi.__createElement("div", { class: "awsui-side-navigation" },
            header,
            list));
    }
});
//# sourceMappingURL=index.js.map